import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMapList, likeMap, unlikeMap, type MapSummary } from '../apiClient/maps';
import { useAuth } from '../auth/AuthProvider';
import {
  chartDurationSeconds,
  formatDuration,
  stripVariantSuffix,
} from '@shared/chart';
import { parseVariantSlug, variantLabel } from '@shared/gameTypes';
import { saveModePref } from '../game/modePref';
import { modePath } from '../game/modeRouting';
import './browse.css';
import './play.css';

type SortOption = 'likes' | 'recent' | 'difficulty' | 'plays';

export function BrowsePage() {
  const { user } = useAuth();
  const [maps, setMaps] = useState<MapSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortOption>('likes');
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState<number | undefined>(undefined);
  const [includeBuiltin, setIncludeBuiltin] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // BrowsePage today is Pulse-only — Phase 7 will add a `?mode=` filter
  // so this page can be reused for Circles. Until then, landing here
  // means the player is browsing Pulse maps; persist that so the
  // TopNav and picker treat them as a Pulse regular.
  useEffect(() => {
    saveModePref('pulse');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMapList({
        sort,
        search: debouncedSearch || undefined,
        difficulty,
        includeBuiltin,
        limit: 60,
      });
      setMaps(res.maps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load maps');
    } finally {
      setLoading(false);
    }
  }, [sort, debouncedSearch, difficulty, includeBuiltin]);

  useEffect(() => {
    void load();
  }, [load]);

  const onToggleLike = async (m: MapSummary) => {
    const optimistic = !m.liked;
    setMaps((prev) =>
      prev.map((x) =>
        x.id === m.id
          ? { ...x, liked: optimistic, likeCount: x.likeCount + (optimistic ? 1 : -1) }
          : x,
      ),
    );
    try {
      const fn = optimistic ? likeMap : unlikeMap;
      const result = await fn(m.id);
      setMaps((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, liked: result.liked, likeCount: result.count } : x)),
      );
    } catch (err) {
      console.warn('Like toggle failed:', err);
      setMaps((prev) =>
        prev.map((x) =>
          x.id === m.id
            ? { ...x, liked: m.liked, likeCount: m.likeCount }
            : x,
        ),
      );
      setError(err instanceof Error ? err.message : 'Failed to update like');
    }
  };

  const filtered = useMemo(() => maps, [maps]);
  // True when the user has tweaked away from defaults; used to decide whether
  // an empty result should offer "Clear filters" or "Build the first one".
  const hasActiveFilters =
    debouncedSearch.length > 0 ||
    difficulty !== undefined ||
    !includeBuiltin ||
    sort !== 'likes';

  return (
    <div className="browse-page" data-testid="browse-page">
      <header className="browse-header">
        <div>
          <h2>Browse maps</h2>
          <p className="muted">{filtered.length} maps</p>
        </div>
        <Link to={modePath('pulse', 'build')} className="btn btn-primary">
          Make your own
        </Link>
      </header>

      <p className="browse-intro muted">
        Free Pulse rhythm-game maps with custom songs — community-made charts you
        can play in your browser, no download. Or build your own.
      </p>

      <div className="browse-controls card">
        <input
          type="text"
          placeholder="Search title or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="browse-search"
          aria-label="Search maps by title or description"
          data-testid="browse-search"
        />
        <div className="browse-filter-group">
          <label className="muted" htmlFor="sort-select">
            Sort
          </label>
          <select
            id="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            data-testid="browse-sort"
          >
            <option value="likes">Most liked</option>
            <option value="recent">Newest</option>
            <option value="plays">Most played</option>
            <option value="difficulty">Hardest</option>
          </select>
        </div>
        <div className="browse-filter-group">
          <label className="muted" htmlFor="diff-select">
            Difficulty
          </label>
          <select
            id="diff-select"
            value={difficulty ?? 'any'}
            onChange={(e) =>
              setDifficulty(e.target.value === 'any' ? undefined : Number(e.target.value))
            }
            data-testid="browse-difficulty"
          >
            <option value="any">Any</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
        </div>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={includeBuiltin}
            onChange={(e) => setIncludeBuiltin(e.target.checked)}
            data-testid="browse-builtin"
          />
          <span>Show official tracks</span>
        </label>
      </div>

      {error && <div className="error-box">{error}</div>}
      {loading && filtered.length === 0 && (
        <div className="browse-grid" data-testid="browse-skeleton">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="browse-card browse-card-skeleton">
              <div className="browse-card-thumb" />
              <div className="browse-card-body">
                <div className="skeleton-line skeleton-title" />
                <div className="skeleton-line skeleton-meta" />
                <div className="skeleton-line skeleton-desc" />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="browse-grid">
        {filtered.map((m) => {
          // Variant pills only ever apply to builtins. UGC slugs are
          // user-titled and run through `slugify` (lower-case + dashes),
          // so any user map titled "Going Hard" / "Try Extreme" / etc.
          // would slug-suffix-match `-hard` / `-extreme` and be
          // mislabelled as a Hard/Extreme variant pill — Standard maps
          // legitimately deserve a `Standard` pill but UGC has no
          // `variant` concept at all. Gate on `isBuiltin` so the pill
          // appears only where the suffix is authoritative.
          const variant = m.isBuiltin ? parseVariantSlug(m.slug).variant : null;
          const bareTitle = variant
            ? stripVariantSuffix({ title: m.title, variant })
            : m.title;
          return (
          <article
            key={m.id}
            className="browse-card"
            data-testid={`browse-card-${m.slug}`}
            style={
              {
                '--map-bg': m.palette.bg,
                '--map-bg-accent': m.palette.bgAccent,
                '--map-pad': m.palette.pad,
                '--map-player': m.palette.player,
              } as React.CSSProperties
            }
          >
            <Link to={modePath('pulse', `play/${m.slug}`)} className="browse-card-thumb">
              <div className="thumb-bg" />
              <div className="thumb-rail thumb-rail-top" />
              <div className="thumb-rail thumb-rail-bottom" />
              <div className="thumb-player" />
              {m.isBuiltin && <span className="thumb-badge">Official</span>}
            </Link>
            <div className="browse-card-body">
              <div className="browse-card-row">
                <Link to={modePath('pulse', `play/${m.slug}`)} className="browse-card-title">
                  {bareTitle}
                  {variant && (
                    <>
                      {' '}
                      <span
                        className={`variant-badge variant-badge-sm variant-badge-${variant}`}
                        data-testid={`browse-variant-${m.slug}`}
                      >
                        {variantLabel(variant).toUpperCase()}
                      </span>
                    </>
                  )}
                </Link>
                <button
                  type="button"
                  className={`like-btn ${m.liked ? 'liked' : ''}`}
                  onClick={() => void onToggleLike(m)}
                  aria-label={m.liked ? 'Unlike' : 'Like'}
                  aria-pressed={m.liked}
                  data-testid={`like-${m.slug}`}
                >
                  <span className="heart">{m.liked ? '\u2665' : '\u2661'}</span>
                  <span>{m.likeCount.toLocaleString()}</span>
                </button>
              </div>
              <div className="browse-card-meta">
                <span>{m.bpm.toFixed(0)} BPM</span>
                <span title="Duration">
                  {formatDuration(chartDurationSeconds(m.bpm, m.totalBeats))}
                </span>
                <span className="diff-pips">
                  {Array.from({ length: 5 }, (_, i) => (
                    <span key={i} className={i < m.difficulty ? 'pip pip-on' : 'pip'} />
                  ))}
                </span>
                <span className="muted">{m.playCount.toLocaleString()} plays</span>
              </div>
              {m.description && <p className="browse-card-desc">{m.description}</p>}
              {!m.isBuiltin && user?.uid === m.authorUid && (
                <Link to={modePath('pulse', `build/${m.slug}`)} className="muted faint browse-edit-link">
                  Edit your map
                </Link>
              )}
            </div>
          </article>
          );
        })}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="empty-state">
          {error ? (
            <>
              <p className="muted">Couldn&apos;t load maps right now.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void load()}
                data-testid="retry-load"
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <p className="muted">No maps match your filters.</p>
              {hasActiveFilters ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setSearch('');
                    setDifficulty(undefined);
                    setIncludeBuiltin(true);
                    setSort('likes');
                  }}
                  data-testid="clear-filters"
                >
                  Clear filters
                </button>
              ) : (
                <Link to={modePath('pulse', 'build')} className="btn">
                  Build the first one
                </Link>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
